import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { DisplayNameProvider } from './context/DisplayNameContext';
import { trackPageView } from './analytics';
import { resetScroll } from './lenis';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import WorkspacePage from './pages/WorkspacePage';
import ApiTesterPage from './pages/ApiTesterPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import PomodoroPage from './pages/PomodoroPage';
import SnippetsPage from './pages/SnippetsPage';
import DsaPage from './pages/DsaPage';
import PlacementsPage from './pages/PlacementsPage';
import DevCardPage from './pages/DevCardPage';
import ResourcesPage from './pages/ResourcesPage';
import RoadmapsPage from './pages/RoadmapsPage';

function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Land at the top of every new route — Lenis keeps scroll position otherwise.
  useEffect(() => {
    resetScroll();
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Public content routes — indexable by crawlers, rendered in the app
            shell with a trimmed nav. Keep in sync with PUBLIC_PATHS in Layout. */}
        <Route element={<Layout />}>
          <Route path="/roadmaps" element={<RoadmapsPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/placements" element={<PlacementsPage />} />
        </Route>

        {/* Protected routes with Layout nav */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/workspace"  element={<WorkspacePage />} />
          <Route path="/api-tester" element={<ApiTesterPage />} />
          <Route path="/profile"   element={<ProfilePage />} />
          <Route path="/pomodoro"  element={<PomodoroPage />} />
          <Route path="/snippets"  element={<SnippetsPage />} />
          <Route path="/dsa"       element={<DsaPage />} />
          <Route path="/dev-card" element={<DevCardPage />} />
        </Route>

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DisplayNameProvider>
          <AnimatedRoutes />
        </DisplayNameProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
